import { useEffect, useState, type FormEvent } from "react";
import type { PlantRow, PlantSymbol } from "@garden/types/db";
import { supabase } from "../auth";
import { createPlant } from "../db/plants";
import type { MappedLocation } from "../lib/mapData";
import type { Polygon } from "../lib/geo";
import { SymbolPicker } from "./SymbolPicker";
import { BedPositionPicker } from "./BedPositionPicker";
import "./LocationCreateModal.css";
import "./AddPlantOnMapModal.css";

interface AddPlantOnMapModalProps {
  location: MappedLocation;
  /** Polygon of `location` (caller has already validated it). */
  polygon: Polygon;
  /** Normalised 0..1 position within the polygon's bbox where the user clicked. */
  initialPosition: { x: number; y: number };
  onClose: () => void;
  onCreated: (plant: PlantRow) => void;
}

export function AddPlantOnMapModal({
  location,
  polygon,
  initialPosition,
  onClose,
  onCreated,
}: AddPlantOnMapModalProps) {
  const [name, setName] = useState("");
  const [scientific, setScientific] = useState("");
  const [symbol, setSymbol] = useState<PlantSymbol | null>(null);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(initialPosition);
  const [plantedDate, setPlantedDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!supabase) {
      setError("Supabase not configured.");
      return;
    }
    if (!name.trim()) {
      setError("Common name is required.");
      return;
    }
    if (!symbol) {
      setError("Pick a map symbol so the pin can render.");
      return;
    }
    if (!position) {
      setError("Click on the bed mini-map to set the pin position.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const plant = await createPlant(supabase, {
        common_name: name.trim(),
        scientific_name: scientific.trim() || null,
        location_id: location.id,
        position,
        symbol,
        planted_date: plantedDate || null,
        status: "alive",
      });
      onCreated(plant);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create plant.");
      setSubmitting(false);
    }
  };

  return (
    <div className="Modal__backdrop" onClick={onClose} role="presentation">
      <div
        className="Modal AddPlantOnMapModal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-plant-title"
      >
        <h2 id="add-plant-title" className="Modal__title">
          Add plant in {location.name}
        </h2>
        <p className="Modal__subtitle">
          Position is set from where you clicked — re-click on the mini-map to
          adjust before saving.
        </p>

        <form onSubmit={handleSubmit} className="Modal__form AddPlantOnMapModal__form">
          <label className="Modal__field">
            <span>Common name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
              placeholder="Climbing rose"
            />
          </label>

          <label className="Modal__field">
            <span>Scientific name <em>optional</em></span>
            <input
              type="text"
              value={scientific}
              onChange={(e) => setScientific(e.target.value)}
              placeholder="Rosa 'Pierre Ronsard'"
            />
          </label>

          <label className="Modal__field">
            <span>Planted date <em>optional</em></span>
            <input
              type="date"
              value={plantedDate}
              onChange={(e) => setPlantedDate(e.target.value)}
            />
          </label>

          <div className="Modal__field">
            <span>Map symbol</span>
            <SymbolPicker value={symbol} onChange={setSymbol} />
          </div>

          <div className="Modal__field">
            <span>Position in {location.name}</span>
            <BedPositionPicker
              polygon={polygon}
              kind={location.kind}
              position={position}
              symbol={symbol}
              onChange={setPosition}
            />
          </div>

          {error && <p className="Modal__error">{error}</p>}

          <div className="Modal__actions">
            <button
              type="button"
              className="PlantForm__btn PlantForm__btn--ghost"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="PlantForm__btn PlantForm__btn--primary"
              disabled={submitting}
            >
              {submitting ? "Adding…" : "Add plant"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
